import { Company, SplitTest } from '@/types/company.type';
import DeviceAndGeoDetector from '@/utils/device-detector.utils';

export type CompanyRegionState = {
  isUEMember: boolean;
  isEeaOrUkCompany: boolean;
};

export const SPLIT_TEST_COUNTRY_KEY = 'country';

export function getCountryCodeFromSplitTest(
  splitTest: SplitTest[] | undefined,
): string | null {
  const value = splitTest?.find(entry => entry.name === SPLIT_TEST_COUNTRY_KEY && entry.value)?.value;
  if (typeof value !== 'string' || !value.trim()) return null;

  const normalized = DeviceAndGeoDetector.normalizeCountryCode(value);
  return normalized === 'unknown' ? null : normalized;
}

export function getEuStateFromSplitTestCountry(
  splitTest: SplitTest[] | undefined,
): CompanyRegionState | null {
  const countryCode = getCountryCodeFromSplitTest(splitTest);
  return getEuStateFromCountryCode(countryCode);
}

export function getEuStateFromCountryCode(
  countryCode: string | null | undefined,
): CompanyRegionState | null {
  const normalized = DeviceAndGeoDetector.normalizeCountryCode(countryCode ?? '');
  if (!normalized || normalized === 'unknown') return null;

  return {
    isUEMember: DeviceAndGeoDetector.isCountryInEU(normalized),
    isEeaOrUkCompany: DeviceAndGeoDetector.isCountryInEEAOrUK(normalized),
  };
}

export async function resolveEuStateFromCompany(
  company: Company | null,
  options?: { checkSignificantChange?: boolean },
): Promise<CompanyRegionState | null> {
  if (!company) return null;

  const fromCustom = company.companySettings?.custom;
  if (typeof fromCustom?.isUEMember === 'boolean' && typeof fromCustom?.isEeaOrUkCompany === 'boolean') {
    return {
      isUEMember: fromCustom.isUEMember,
      isEeaOrUkCompany: fromCustom.isEeaOrUkCompany,
    };
  }

  const fromSplitTestCountry = getEuStateFromSplitTestCountry(company.splitTest);
  if (fromSplitTestCountry) return fromSplitTestCountry;

  const { country } = await DeviceAndGeoDetector.initialize(options?.checkSignificantChange ?? true);
  return getEuStateFromCountryCode(country);
}

export function getEuStateFromCompanySettings(
  company: Company | null,
): CompanyRegionState | null {
  if (!company) return null;

  const fromCustom = company.companySettings?.custom;
  if (typeof fromCustom?.isUEMember === 'boolean' && typeof fromCustom?.isEeaOrUkCompany === 'boolean') {
    return {
      isUEMember: fromCustom.isUEMember,
      isEeaOrUkCompany: fromCustom.isEeaOrUkCompany,
    };
  }

  return getEuStateFromSplitTestCountry(company.splitTest);
}
